"use client";

import { c as _c } from "react/compiler-runtime";
import { isAuthenticated, logout } from "@/lib/auth-utils";
import { RememberMe } from "@/lib/remember-me";
import { TokenRefresh } from "@/lib/token-refresh";
import { fetchFeatureFlags } from "@/features/feature-flags/api/feature-flag-queries";
import { queryKeys } from "@/react-query/query-keys";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * SessionProvider - Initializes and manages automatic token refresh
 * Features:
 * - Background auto-refresh every 45 minutes
 * - Proactive token check when user returns to tab after being idle
 * - Automatic refresh with 3 retry attempts
 * - Logout on persistent refresh failures
 */
export function SessionProvider(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t1 = () => {
      RememberMe.initialize();
      if (isAuthenticated()) {
        queryClient.prefetchQuery({
          queryKey: queryKeys.featureFlags(),
          queryFn: fetchFeatureFlags
        });
      }
      const handleVisibilityChange = _temp;
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        RememberMe.stopAutoRefresh();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t2 = [queryClient];
    $[0] = queryClient;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <>{children}</>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
async function _temp() {
  if (document.visibilityState === "visible") {
    if (!isAuthenticated()) {
      return;
    }
    if (!RememberMe.isSessionActive()) {
      logout();
      return;
    }
    if (TokenRefresh.isTokenExpired()) {
      const success = await TokenRefresh.refreshTokens();
      if (!success) {
        logout();
      }
    }
  }
}